import { DEFAULT_APPEARANCE } from '@/theme/engine.mjs';
import { rupiah } from "@/lib/format";
import { toast } from "sonner";
import { loadReceiptSettings } from './receipt-settings';
import { receiptGeometry, receiptBoxCss } from './receipt-geometry';
import receiptFontUrl from '@/assets/receipt-fonts/RobotoCondensed-Latin.woff2';

const escape = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));

export function closingPaper(settings = {}) {
  return receiptGeometry(settings).paper;
}

export function buildClosingHtml(shift, summary, storeName, settings = {}) {
  const s = summary || shift?.summary || {};
  const openedAt = shift?.opened_at ? new Date(shift.opened_at).toLocaleString("id-ID") : "-";
  const closedAt = shift?.closed_at ? new Date(shift.closed_at).toLocaleString("id-ID") : new Date().toLocaleString("id-ID");
  const rows = [
    ["Kasir", shift?.user_name || "-"],
    ["Buka", openedAt],
    ["Tutup", closedAt],
    ["", ""],
    ["Jumlah Transaksi", s.tx_count ?? 0],
    ["Item Terjual", s.items_sold ?? 0],
    ["Total Penjualan", rupiah(s.revenue || 0)],
    ["", ""],
    ["Tunai", rupiah(s.tunai || 0)],
    ["Transfer", rupiah(s.transfer || 0)],
    ["QRIS", rupiah(s.qris || 0)],
    ["Kembalian", rupiah(s.change || 0)],
    ["Piutang (baru)", rupiah(s.piutang || 0)],
    ["Pelunasan Piutang", rupiah(s.pelunasan || 0)],
    ["", ""],
    ["Saldo Awal", rupiah(s.opening_cash ?? shift?.opening_cash ?? 0)],
    ["Kas Seharusnya", rupiah(shift?.cash_expected ?? s.cash_expected ?? 0)],
    ["Kas Dihitung", rupiah(shift?.cash_counted ?? 0)],
    ["Selisih", rupiah(shift?.selisih ?? 0)],
    ...(shift?.variance_reason ? [["Alasan Selisih", shift.variance_reason]] : []),
    ...(shift?.denominations?.length ? [["", ""], ["Rincian Pecahan", ""], ...shift.denominations.map(row => [`${rupiah(row.value)} x ${row.count}`, rupiah(row.total)])] : []),
  ];
  const body = rows.map(([k, v]) =>
    k === "" ? `<tr><td colspan="2"><hr/></td></tr>` : `<tr${['Kas Seharusnya','Kas Dihitung','Selisih'].includes(k) ? ' class="closing-total"' : ''}><td>${escape(k)}</td><td style="text-align:right">${escape(v)}</td></tr>`
  ).join("");
  const width = closingPaper(settings);
  return `<!doctype html><html lang="id"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>Closing Shift</title>
    <style>
    @font-face{font-family:'Roboto Condensed';src:url('${receiptFontUrl}') format('woff2');font-style:normal;font-weight:100 900;font-display:block}
    @page{margin:0;size:auto}*{box-sizing:border-box}html,body{margin:0;padding:0;background:white;color:black;min-height:0}
    #closing-receipt{${receiptBoxCss(settings)}font:400 12px/1.35 'Roboto Condensed',Arial,sans-serif;font-variant-numeric:tabular-nums;overflow-wrap:anywhere}
    h2{font-size:20px;line-height:1.15;text-align:center;margin:0 0 4px}p{text-align:center;margin:2px 0}table{width:100%;table-layout:fixed;border-collapse:collapse;font-size:12px}td{padding:2px 0;vertical-align:top;white-space:pre-wrap;overflow-wrap:anywhere}td:first-child{width:45%;padding-right:6px}td:last-child{width:55%}tr{break-inside:avoid}.closing-total{font-size:14px;font-weight:700}hr{border:0;border-top:1px dashed black;margin:4px 0}
    .print-help{max-width:550px;padding:12px;font:13px/1.4 Arial;background:#f0fdf4}.print-help button{padding:7px 12px;margin-bottom:6px}.print-help p{text-align:left}.closing-footer{margin:3px 0 0}
    @media print{.print-help{display:none!important}html,body{height:auto!important;min-height:0!important}#closing-receipt{margin:0 auto;break-after:auto}*{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
    </style><style id="closing-page-size"></style></head><body>
    <nav class="print-help"><button type="button" onclick="window.print()">Cetak ulang / Simpan PDF</button><p>Kertas thermal ${width} mm. Pilih printer Epson, margin Tidak ada, skala 100%, dan nonaktifkan header/footer. Bila driver tetap memakai halaman panjang, pilih ukuran kertas roll yang sesuai.</p><p>Cetak tanpa dialog: buka shortcut desktop POS Cetak Langsung, bukan aplikasi dari tombol Instal Chrome.</p></nav>
    <main id="closing-receipt"><h2>${escape(storeName || DEFAULT_APPEARANCE.branding.name)}</h2><p>LAPORAN TUTUP SHIFT</p><hr/><table>${body}</table><hr/><p class="closing-footer">Terima kasih</p></main>
    <script>
    function fitClosingPage(){
      var receipt=document.getElementById('closing-receipt');
      var height=Math.max(25,Math.ceil(receipt.getBoundingClientRect().height*25.4/96)+2);
      document.getElementById('closing-page-size').textContent='@page{size:${width}mm '+Math.min(1500,height)+'mm;margin:0}';
      document.documentElement.dataset.closingHeightMm=String(height);
    }
    window.addEventListener('beforeprint',fitClosingPage);
    window.onload=async function(){
      if(document.fonts){try{await Promise.all([document.fonts.load('400 12px "Roboto Condensed"'),document.fonts.load('700 14px "Roboto Condensed"'),document.fonts.load('700 20px "Roboto Condensed"')]);await document.fonts.ready;}catch(e){}}
      fitClosingPage();window.setTimeout(function(){window.focus();window.print();},150);
    };
    </script></body></html>`;
}

export function printClosing(shift, summary, storeName) {
  const w = window.open("", "_blank", "width=380,height=640");
  if (!w) { toast.error("Popup diblokir. Izinkan popup untuk mencetak."); return false; }
  w.opener = null;
  const write = (settings) => {
    if (w.closed) return;
    w.document.open(); w.document.write(buildClosingHtml(shift, summary, storeName, settings)); w.document.close();
    w.focus();
  };
  w.document.body.textContent = 'Menyiapkan laporan closing…';
  loadReceiptSettings(shift?.store_id).then(write);
  return true;
}
